import { JSX } from 'react';

// components
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtTag } from '@/components/atoms/at-tag/at-tag';

// types
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import { EClickableSize } from '@/components/atoms/at-clickable/at-clickable.types';
import { IMlstudioListProps } from './ml-studio-list.types';
import { IDesignStudio } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';

// hooks/utilities
import useBreakpoint from '@/hooks/useBreakpoint';

/**
 * Sorts design studios alphabetically by "State - Name".
 * Uses fallback values: \`CsState || State\` and \`CsName || Name\`
 * to ensure consistency across CMS and API data.
 */
export const sortStudios = (studioData: IDesignStudio[]) => {
  const sortedStudios = studioData.sort((a, b) => {
    const aStr = `${a?.CsState || a.State} - ${a?.CsName || a.Name}`;
    const bStr = `${b?.CsState || b.State} - ${b?.CsName || b.Name}`;
    return aStr.localeCompare(bStr); // cleaner than if/else
  });
  return sortedStudios;
};

/**
 * Renders a scrollable, alphabetized list of design studios.
 * Used exclusively in the mapping tool to allow users to browse and select studios.
 *
 * Hovering over an item triggers map highlighting (desktop only).
 * Clicking an item selects the studio and opens the detail card.
 *
 */

export const MlStudioList = ({
  allStudios,
  onItemClick,
  onItemHover,
}: IMlstudioListProps): JSX.Element => {
  const bp = useBreakpoint();
  const isMobile = ['xs', 'sm'].includes(bp);

  const alphaStudios = sortStudios(allStudios);

  const handleLocationSelected = (studio: IDesignStudio) => {
    onItemClick(studio.Id);
  };

  const handleHover = (studio: IDesignStudio) => {
    if (isMobile) return;

    onItemHover?.(studio.Id);
  };

  return (
    <div className="w-full max-w-598">
      <AtHeader element="p" variant="h6" className="pb-6 pl-7">
        Browse the list
      </AtHeader>
      <ul className="custom-scrollbar mx-4 flex max-h-[20.50rem] flex-col gap-y-2 overflow-y-auto lg:max-h-[26.25rem]">
        {alphaStudios.map((studio) => {
          return (
            <li className="w-fit px-3" key={studio.Id}>
              <AtTag
                variant={ETagVariants.LOCATION}
                size={EClickableSize.SM}
                onMouseEnter={() => handleHover(studio)}
                onMouseLeave={() => onItemHover?.(null)}
                onClick={() => handleLocationSelected(studio)}
              >
                {studio?.CsState || studio.State} -{' '}
                {studio?.CsName || studio.Name}
              </AtTag>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
